// Clerk blocks personal email providers (gmail, outlook, ...) via a Dashboard
// restriction. It rejects with code `not_allowed_access` on both sign-in and
// sign-up, and Clerk ships NO default copy for that key (en-US defines it as
// undefined), so the prebuilt <SignIn />/<SignUp /> cards fall through to the
// raw server message — "<address> is not allowed to access this application."
// That names the address but never the reason or a way forward.
//
// Defining the key here gives Clerk something to render instead, everywhere
// the prebuilt components surface it, without giving up the hosted flow.
//
// Only codes that exist as keys in Clerk's UnstableErrors type can be reworded
// this way — an unrecognised key is silently ignored, so verify against
// @clerk/types before adding one. Notably `user_banned` has NO key, so the ban
// message ("You have been banned. If you think this was by mistake, please
// contact support.") is not customizable here. SUPPORT_LINE_PREFIX below
// exists to answer the "which support?" question that message leaves open.
//
// Clerk renders these as plain text, so the support address can't be a mailto
// link here — the support line in SignInPage/SignUpPage carries the clickable
// version.

// i18n note: this module is NOT a component, so it uses the i18n instance
// directly rather than a hook. `base` carries the locale's @clerk/localizations
// bundle (esES / itIT / undefined for en) and our copy is merged ON TOP of it.
// Everything here patches a hole upstream left — see gapFills() below for the
// keys Clerk leaves undefined in the bundles we ship.
import i18n from 'i18n';

// Flow-neutral on purpose: the same line renders on both sign-in and sign-up.
export const supportLinePrefix = () => i18n.t('auth.supportLinePrefix', 'Need help?');

// Keys @clerk/localizations leaves `undefined` in one or more of the bundles we
// ship. An undefined key falls through to en-US, so the prebuilt cards render
// English inside an otherwise translated flow — "Continue to Carbon Glance" on
// a Spanish sign-in card is the reported symptom, and it is not the only one.
//
// Each entry is a dotted path into Clerk's localization object. The value is
// used ONLY where upstream has nothing (see fillGap), so Clerk's own wording
// keeps winning and every key reverts to upstream the moment they translate it.
//
// Sign-in and sign-up share wording for the same states, which is why several
// paths resolve to one catalogue key.
//
// Deliberately NOT exhaustive. The `alternativePhoneCodeProvider` family leaks
// the same way in es/it/pl, but it belongs to phone-code sign-in (WhatsApp/SMS
// OTP) and we configure no such provider — those cards cannot render, so
// translating them would be dead copy. Re-check against @clerk/localizations if
// a phone-code provider is ever enabled.
//
// Verified against @clerk/localizations 4.13.8 by diffing every bundle we ship
// against en-US. `??` semantics mean a stale entry here degrades to "unused",
// never to a regression.
const gapFills = () => {
  // `{{applicationName}}` is CLERK's interpolation token, not ours: i18next has
  // to return it untouched for Clerk to substitute the app name. That is
  // i18next's default for a variable the call site does not supply, but it is a
  // default worth pinning — `skipOnVariables: false` would blank the brand name
  // and leave a dangling "Continue to".
  const continueTo = i18n.t('auth.clerkContinueTo', 'Continue to {{applicationName}}', {
    interpolation: { skipOnVariables: true },
  });
  // Shown when an identifier resolves to more than one enterprise (SSO)
  // connection and the user has to pick which one to continue with.
  const enterpriseTitle = i18n.t(
    'auth.clerkEnterpriseAccountTitle',
    'Choose your enterprise account',
  );
  const enterpriseSubtitle = i18n.t(
    'auth.clerkEnterpriseAccountSubtitle',
    'Select the enterprise account with which you wish to continue.',
  );
  // Clerk's bot/abuse interstitial. It can front ANY sign-in or sign-up
  // attempt, which is what makes an English-only version of it conspicuous.
  const protectTitle = i18n.t('auth.clerkProtectCheckTitle', 'Verifying your request');
  const protectSubtitle = i18n.t(
    'auth.clerkProtectCheckSubtitle',
    'Please wait while we verify your request.',
  );
  const protectLoading = i18n.t('auth.clerkProtectCheckLoading', 'Loading…');
  const protectRetry = i18n.t('auth.clerkProtectCheckRetry', 'Try again');

  return {
    'signIn.start.titleCombined': continueTo,

    'signIn.enterpriseConnections.title': enterpriseTitle,
    'signIn.enterpriseConnections.subtitle': enterpriseSubtitle,
    'signUp.enterpriseConnections.title': enterpriseTitle,
    'signUp.enterpriseConnections.subtitle': enterpriseSubtitle,

    'signIn.protectCheck.title': protectTitle,
    'signIn.protectCheck.subtitle': protectSubtitle,
    'signIn.protectCheck.loading': protectLoading,
    'signIn.protectCheck.retryButton': protectRetry,
    'signUp.protectCheck.title': protectTitle,
    'signUp.protectCheck.subtitle': protectSubtitle,
    'signUp.protectCheck.loading': protectLoading,
    'signUp.protectCheck.retryButton': protectRetry,

    // Password rejected by Clerk's breach/reuse checks. `passwordCompromised`
    // is the successor to `passwordPwned`, and only the OLD key carries es/it
    // translations upstream — the rename is exactly how this gap appeared.
    'signIn.passwordCompromised.title': i18n.t(
      'auth.clerkPasswordCompromised',
      'Password compromised',
    ),
    'signIn.passwordUntrusted.title': i18n.t('auth.clerkPasswordUntrusted', 'Password untrusted'),
  };
};

// Write `value` at `path`, but only where nothing is there already. Copies each
// object on the way down so `base` is never mutated: Clerk keeps a reference to
// the bundle it was handed, and the bundles are module-level singletons shared
// across every locale switch.
const fillGap = (target, path, value) => {
  const [head, ...rest] = path;
  if (rest.length === 0) {
    if (target[head] === undefined) target[head] = value;
    return;
  }
  target[head] = { ...target[head] };
  fillGap(target[head], rest, value);
};

export function buildClerkLocalization(supportEmail, base) {
  const message = supportEmail
    ? i18n.t(
        'auth.notAllowedAccessWithSupport',
        "Please use your work email address. Personal email providers (Gmail, Outlook, Yahoo, etc.) aren't supported. If your company uses one of these providers for its email, contact {{supportEmail}} and we'll get you set up.",
        { supportEmail },
      )
    : i18n.t(
        'auth.notAllowedAccess',
        "Please use your work email address. Personal email providers (Gmail, Outlook, Yahoo, etc.) aren't supported.",
      );

  const localization = { ...(base || {}) };
  for (const [path, value] of Object.entries(gapFills())) {
    fillGap(localization, path.split('.'), value);
  }

  return {
    ...localization,
    unstable__errors: {
      ...localization.unstable__errors,
      // Ours by intent rather than a gap fill: even if Clerk ships copy for
      // this code one day, the product-specific "use your work email" answer is
      // the one that belongs here.
      not_allowed_access: message,
    },
  };
}
